import React, { useState } from 'react';
import { motion, MotionConfig, Variants } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
};

const stagger: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.09, delayChildren: 0.04 } },
};

const inView = {
  initial: 'hidden' as const,
  whileInView: 'show' as const,
  viewport: { once: true, margin: '-80px' },
};

/**
 * The signature artifact: two strongest opposing arguments separated by a
 * center hairline. PRO is locked to steel-blue, CON to vermilion — these
 * colors are semantic (debate sides) and never decorative elsewhere.
 *
 * Interactive: visitors can cast a demo vote and watch the bar tug.
 */
const DebateCard: React.FC<{
  topic: string;
  pro: string;
  con: string;
  proShare?: number;
  votes?: number;
  interactive?: boolean;
}> = ({ topic, pro, con, proShare = 58, votes = 1204, interactive = false }) => {
  const [voted, setVoted] = useState<'pro' | 'con' | null>(null);

  // Nudge the share when the visitor casts a demo vote
  const share = voted === 'pro' ? proShare + 3 : voted === 'con' ? proShare - 3 : proShare;
  const total = voted ? votes + 1 : votes;

  return (
    <div className="card-frame overflow-hidden">
      {/* Topic header */}
      <div className="flex items-center justify-between gap-4 border-b border-black/[0.06] px-5 py-4 sm:px-6">
        <span className="truncate font-serif text-base text-charcoal sm:text-lg">{topic}</span>
        <span className="flex-shrink-0 font-mono text-[10px] uppercase tracking-wider text-ink-muted">
          {voted ? 'Vote recorded' : 'Resolved'}
        </span>
      </div>

      {/* Two sides */}
      <div className="grid grid-cols-1 sm:grid-cols-2">
        <div
          className={`group border-b border-black/[0.06] p-5 transition-colors duration-300 sm:border-b-0 sm:border-r sm:p-6 ${
            voted === 'pro' ? 'bg-steel-blue/[0.06]' : interactive ? 'hover:bg-steel-blue/[0.04]' : ''
          }`}
        >
          <div className="mb-2.5 flex items-center gap-2">
            <span className="h-3 w-1 rounded-full bg-steel-blue" />
            <span className="font-mono text-xs font-medium uppercase tracking-wider text-steel-blue">
              For
            </span>
          </div>
          <p className="text-pretty text-[15px] leading-relaxed text-charcoal/80">{pro}</p>
          {interactive && (
            <button
              onClick={() => setVoted(voted === 'pro' ? null : 'pro')}
              className={`mt-4 rounded-full border px-4 py-1.5 font-mono text-xs transition-all duration-200 ${
                voted === 'pro'
                  ? 'border-steel-blue bg-steel-blue text-white'
                  : 'border-steel-blue/30 text-steel-blue hover:border-steel-blue hover:bg-steel-blue/10'
              }`}
            >
              {voted === 'pro' ? '✓ Voted for' : 'Vote for'}
            </button>
          )}
        </div>
        <div
          className={`group p-5 transition-colors duration-300 sm:p-6 ${
            voted === 'con' ? 'bg-vermilion/[0.06]' : interactive ? 'hover:bg-vermilion/[0.04]' : ''
          }`}
        >
          <div className="mb-2.5 flex items-center gap-2">
            <span className="h-3 w-1 rounded-full bg-vermilion" />
            <span className="font-mono text-xs font-medium uppercase tracking-wider text-vermilion">
              Against
            </span>
          </div>
          <p className="text-pretty text-[15px] leading-relaxed text-charcoal/80">{con}</p>
          {interactive && (
            <button
              onClick={() => setVoted(voted === 'con' ? null : 'con')}
              className={`mt-4 rounded-full border px-4 py-1.5 font-mono text-xs transition-all duration-200 ${
                voted === 'con'
                  ? 'border-vermilion bg-vermilion text-white'
                  : 'border-vermilion/30 text-vermilion hover:border-vermilion hover:bg-vermilion/10'
              }`}
            >
              {voted === 'con' ? '✓ Voted against' : 'Vote against'}
            </button>
          )}
        </div>
      </div>

      {/* Split vote bar — the tug of war */}
      <div className="border-t border-black/[0.06] px-5 py-4 sm:px-6">
        <div className="mb-2 flex items-center justify-between text-xs">
          <span className="font-mono font-medium text-steel-blue">{share}% For</span>
          <span className="font-mono text-ink-muted">{total.toLocaleString()} votes</span>
          <span className="font-mono font-medium text-vermilion">{100 - share}% Against</span>
        </div>
        <div className="vs-track">
          <motion.div
            className="bg-steel-blue"
            animate={{ width: `${share}%` }}
            transition={{ duration: 0.7, ease: EASE }}
          />
          <span className="w-[3px] flex-shrink-0 bg-white" />
          <motion.div
            className="flex-1 bg-vermilion"
            transition={{ duration: 0.7, ease: EASE }}
          />
        </div>
      </div>
    </div>
  );
};

const SAMPLE_TOPICS = [
  'Should AI development be regulated?',
  'Is remote work better for productivity?',
  'Should college be free?',
  'Is nuclear energy the answer to climate change?',
  'Should social media require age verification?',
  'Is a four-day work week inevitable?',
  'Should billionaires exist?',
  'Is space exploration worth the cost?',
];

const Landing: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <MotionConfig reducedMotion="user">
      <div className="relative isolate w-full bg-white">
        {/* Continuous blueprint grid running the full length of the page */}
        <div aria-hidden className="blueprint-grid pointer-events-none absolute inset-0 z-0" />

        <div className="relative z-10">
          {/* ───────────────────────── Hero ───────────────────────── */}
          <section className="relative">
            <div className="relative mx-auto max-w-6xl px-6 pt-20 pb-14 sm:pt-28 sm:pb-16">
              <motion.div
                initial="hidden"
                animate="show"
                variants={stagger}
                className="mx-auto max-w-3xl text-center"
              >
                <motion.div variants={fadeUp}>
                  <span className="eyebrow inline-flex items-center gap-2 rounded-full border border-black/[0.08] bg-white/80 px-4 py-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-steel-blue" />
                    Structured AI debate
                    <span className="h-1.5 w-1.5 rounded-full bg-vermilion" />
                  </span>
                </motion.div>

                <motion.h1
                  variants={fadeUp}
                  className="mt-7 text-balance font-serif text-5xl leading-[1.02] tracking-tight text-charcoal sm:text-6xl lg:text-[5.25rem]"
                >
                  Argue the{' '}
                  <span className="bg-gradient-to-r from-steel-blue via-steel-blue to-vermilion bg-clip-text text-transparent">
                    best version
                  </span>
                  <br className="hidden sm:block" /> of every idea.
                </motion.h1>

                <motion.p
                  variants={fadeUp}
                  className="mx-auto mt-7 max-w-xl text-pretty text-lg leading-relaxed text-ink-muted sm:text-xl"
                >
                  Two AI agents make the strongest possible case for and against any
                  question. No outrage, no strawmen — just clear, rigorous reasoning.
                  You cast the deciding vote.
                </motion.p>

                <motion.div
                  variants={fadeUp}
                  className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row"
                >
                  <button
                    onClick={() => navigate(user ? '/arena' : '/signup')}
                    className="btn-primary w-full !px-8 !py-3.5 !text-base sm:w-auto"
                  >
                    Start a debate
                  </button>
                  <button
                    onClick={() => navigate('/explore')}
                    className="btn-secondary w-full !px-8 !py-3.5 !text-base sm:w-auto"
                  >
                    Explore debates
                  </button>
                </motion.div>

                <motion.p variants={fadeUp} className="mt-6 font-mono text-[11px] uppercase tracking-eyebrow text-ink-muted/70">
                  Research → Opening → Rebuttal → Closing
                </motion.p>
              </motion.div>

              {/* Centerpiece artifact — try voting */}
              <motion.div
                initial={{ opacity: 0, y: 28 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: EASE, delay: 0.4 }}
                className="mx-auto mt-16 max-w-3xl"
              >
                <DebateCard
                  topic="Should AI development be regulated?"
                  pro="Without shared safety standards, competitive pressure pushes labs toward a race to the bottom — speed wins, caution loses, and the public bears the externalities."
                  con="Premature regulation entrenches incumbents and freezes the field at today's understanding. Innovation has historically advanced fastest with room to experiment."
                  proShare={58}
                  interactive
                />
                <p className="mt-3 text-center font-mono text-[11px] uppercase tracking-eyebrow text-ink-muted/60">
                  ↑ Go ahead — cast a vote
                </p>
              </motion.div>
            </div>

            {/* Topic marquee */}
            <div className="relative border-y border-black/[0.08] bg-white/70 py-4 backdrop-blur-sm">
              <div className="overflow-hidden whitespace-nowrap [mask-image:linear-gradient(90deg,transparent,black_8%,black_92%,transparent)]">
                <div className="marquee-track inline-flex items-center gap-3 pr-3">
                  {[...SAMPLE_TOPICS, ...SAMPLE_TOPICS].map((topic, i) => (
                    <Link
                      key={`${topic}-${i}`}
                      to="/explore"
                      className="inline-flex items-center gap-2.5 rounded-full border border-black/[0.08] bg-white px-4 py-2 font-mono text-xs text-ink-muted transition-colors hover:border-charcoal hover:text-charcoal"
                    >
                      <span className="flex h-2 w-2 overflow-hidden rounded-full">
                        <span className="h-full w-1/2 bg-steel-blue" />
                        <span className="h-full w-1/2 bg-vermilion" />
                      </span>
                      {topic}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* ───────────────────── How it works (drafted cells) ───────────────────── */}
          <section className="mx-auto max-w-6xl px-6 py-20 sm:py-24">
            <motion.div {...inView} variants={fadeUp} className="mx-auto max-w-2xl text-center">
              <span className="eyebrow">How it works</span>
              <h2 className="mt-4 text-balance font-serif text-3xl tracking-tight text-charcoal sm:text-4xl">
                Three steps from question to clarity
              </h2>
            </motion.div>

            <motion.div
              {...inView}
              variants={stagger}
              className="mt-14 grid grid-cols-1 overflow-hidden rounded-2xl border border-black/[0.08] bg-white/80 backdrop-blur-sm md:grid-cols-3 md:divide-x md:divide-black/[0.08]"
            >
              {[
                {
                  n: '01',
                  title: 'Pose a question',
                  body: 'Bring any claim, dilemma, or hot-button topic. The sharper the disagreement, the better the debate.',
                },
                {
                  n: '02',
                  title: 'Watch both sides',
                  body: 'Two AI agents research, then build the strongest case for and against — opening, rebuttal, and closing.',
                },
                {
                  n: '03',
                  title: 'Decide for yourself',
                  body: 'Weigh the arguments, cast your vote, and see where the community lands.',
                },
              ].map((step) => (
                <motion.div
                  key={step.n}
                  variants={fadeUp}
                  className="group border-b border-black/[0.08] p-8 transition-colors duration-300 last:border-b-0 hover:bg-white md:border-b-0"
                >
                  <div className="flex items-baseline justify-between">
                    <span className="font-mono text-xs text-ink-muted/60">{step.n}</span>
                    <span className="h-px w-10 bg-gradient-to-r from-steel-blue to-vermilion opacity-0 transition-opacity duration-300 group-hover:opacity-70" />
                  </div>
                  <h3 className="mt-6 font-serif text-2xl text-charcoal">{step.title}</h3>
                  <p className="mt-3 text-pretty text-[15px] leading-relaxed text-ink-muted">
                    {step.body}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </section>

          {/* ───────────────────── The problem ───────────────────── */}
          <section className="border-t border-black/[0.07]">
            <div className="mx-auto max-w-6xl px-6 py-20 sm:py-24">
              <div className="grid grid-cols-1 gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20">
                <motion.div {...inView} variants={fadeUp}>
                  <span className="eyebrow">The problem</span>
                  <h2 className="mt-4 text-balance font-serif text-3xl leading-tight tracking-tight text-charcoal sm:text-4xl">
                    Today's discourse is built to divide
                  </h2>
                  <p className="mt-5 text-pretty text-lg leading-relaxed text-ink-muted">
                    Feeds reward outrage. Headlines chase clicks. Complex questions
                    collapse into soundbites. Everyone's talking, nobody's listening —
                    and the best arguments rarely survive the noise.
                  </p>
                </motion.div>

                <motion.div {...inView} variants={stagger} className="divide-y divide-hairline self-center">
                  {[
                    {
                      problem: 'Echo chambers',
                      description:
                        'Algorithms feed you what you already believe, hardening bias into identity.',
                    },
                    {
                      problem: 'Strawman arguments',
                      description:
                        'People attack the weakest version of the other side instead of its strongest.',
                    },
                    {
                      problem: 'Emotional manipulation',
                      description:
                        'Fear and outrage drive engagement, drowning out reasoned thought.',
                    },
                  ].map((item) => (
                    <motion.div
                      key={item.problem}
                      variants={fadeUp}
                      className="flex items-baseline gap-4 py-6 first:pt-0 last:pb-0"
                    >
                      <span className="h-1.5 w-1.5 flex-shrink-0 translate-y-1.5 rounded-full bg-vermilion" />
                      <div>
                        <h3 className="text-base font-semibold text-charcoal">{item.problem}</h3>
                        <p className="mt-1 text-pretty text-[15px] leading-relaxed text-ink-muted">
                          {item.description}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              </div>
            </div>
          </section>

          {/* ───────────────────── The difference ───────────────────── */}
          <section className="border-t border-black/[0.07]">
            <div className="mx-auto max-w-6xl px-6 py-20 sm:py-24">
              <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-20">
                <motion.div {...inView} variants={fadeUp}>
                  <span className="eyebrow">Why Steelman</span>
                  <h2 className="mt-4 text-balance font-serif text-3xl leading-tight tracking-tight text-charcoal sm:text-4xl">
                    A different approach to ideas
                  </h2>
                  <p className="mt-5 text-pretty text-lg leading-relaxed text-ink-muted">
                    Watch AI agents present the strongest possible case for both sides.
                    No bias, no theatrics — just structured argument you can actually
                    learn from.
                  </p>

                  <div className="mt-8 divide-y divide-hairline">
                    {[
                      {
                        title: 'AI argues both sides',
                        body: 'No human ego or emotion — only the best case for each position.',
                      },
                      {
                        title: 'Structured format',
                        body: 'Openings, rebuttals, and closings keep every debate focused.',
                      },
                      {
                        title: 'Community voting',
                        body: 'See which arguments land — without a toxic comment section.',
                      },
                    ].map((f) => (
                      <div key={f.title} className="py-4 first:pt-0">
                        <h4 className="text-[15px] font-semibold text-charcoal">{f.title}</h4>
                        <p className="mt-1 text-pretty text-[14px] leading-relaxed text-ink-muted">{f.body}</p>
                      </div>
                    ))}
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.6, ease: EASE }}
                >
                  <DebateCard
                    topic="Is remote work better for productivity?"
                    pro="Removing the commute and open-office noise gives people deep-focus time, and async work forces clearer written communication."
                    con="Spontaneous collaboration, mentorship, and trust are harder to build over video — and they compound over time."
                    proShare={47}
                    votes={862}
                    interactive
                  />
                </motion.div>
              </div>
            </div>
          </section>

          {/* ───────────────────── What you can do ───────────────────── */}
          <section className="border-t border-black/[0.07]">
            <div className="mx-auto max-w-6xl px-6 py-20 sm:py-24">
              <motion.div {...inView} variants={fadeUp} className="mx-auto max-w-2xl text-center">
                <span className="eyebrow">Get started</span>
                <h2 className="mt-4 text-balance font-serif text-3xl tracking-tight text-charcoal sm:text-4xl">
                  What you can do on Steelman
                </h2>
              </motion.div>

              <motion.div
                {...inView}
                variants={stagger}
                className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3"
              >
                {[
                  {
                    title: 'Explore public debates',
                    description:
                      'Browse AI-generated debates on trending topics. Learn from structured arguments without the noise.',
                    action: 'Start watching',
                    link: '/explore',
                  },
                  {
                    title: 'Create private debates',
                    description:
                      'Pose your own questions in the Arena and watch AI argue both sides in real time.',
                    action: user ? 'Open the Arena' : 'Sign in to create',
                    link: user ? '/arena' : '/signin',
                  },
                  {
                    title: 'Vote & share',
                    description:
                      'Decide which side made the stronger case, and share the debates that shifted your thinking.',
                    action: 'Join the community',
                    link: user ? '/explore' : '/signup',
                  },
                ].map((item, i) => (
                  <motion.div
                    key={item.title}
                    variants={fadeUp}
                    className="group flex flex-col rounded-2xl border border-black/[0.07] bg-white/90 p-7 backdrop-blur-sm transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:border-transparent hover:bg-white hover:shadow-frame-lg"
                  >
                    <span className="font-serif text-xl text-charcoal/20">0{i + 1}</span>
                    <h3 className="mt-5 text-xl font-semibold text-charcoal">{item.title}</h3>
                    <p className="mt-2 flex-grow text-pretty text-[15px] leading-relaxed text-ink-muted">
                      {item.description}
                    </p>
                    <Link
                      to={item.link}
                      className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-charcoal transition-colors group-hover:text-steel-blue"
                    >
                      {item.action}
                      <svg
                        className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </Link>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </section>

          {/* ───────────────────── Final CTA (ink panel) ───────────────────── */}
          <section className="px-6 pb-24 pt-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.6, ease: EASE }}
              className="blueprint-grid-dark relative mx-auto max-w-5xl overflow-hidden rounded-[28px] bg-charcoal px-8 py-20 text-center sm:py-24"
            >
              {/* soft brand wash to keep the dark panel from feeling like a hard block */}
              <div
                className="pointer-events-none absolute inset-0 opacity-40"
                style={{
                  background:
                    'radial-gradient(60% 120% at 15% 0%, rgba(58,104,160,0.45), transparent 60%), radial-gradient(60% 120% at 85% 100%, rgba(229,68,45,0.40), transparent 60%)',
                }}
              />
              <div className="relative">
                <h2 className="text-balance font-serif text-4xl leading-tight tracking-tight text-white sm:text-5xl">
                  Ready to think differently?
                </h2>
                <p className="mx-auto mt-5 max-w-lg text-pretty text-lg leading-relaxed text-white/60">
                  Join a community that values nuanced thinking over hot takes.
                </p>
                <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
                  <button
                    onClick={() => navigate(user ? '/arena' : '/signup')}
                    className="w-full rounded-full bg-white px-8 py-4 text-base font-medium text-charcoal transition-all duration-200 ease-out-expo hover:-translate-y-0.5 hover:bg-white/90 sm:w-auto"
                  >
                    Start a debate
                  </button>
                  <button
                    onClick={() => navigate('/explore')}
                    className="w-full rounded-full border border-white/25 px-8 py-4 text-base font-medium text-white transition-all duration-200 ease-out-expo hover:-translate-y-0.5 hover:bg-white/10 sm:w-auto"
                  >
                    Explore debates
                  </button>
                </div>
              </div>
            </motion.div>
          </section>
        </div>
      </div>
    </MotionConfig>
  );
};

export default Landing;
